import { filesLineFromSearch, filesPathFromSearch } from './filesPath';
import { servicePath } from './projectOverview';
import { isBrainPath } from './projectLayout';
import { encodePathSegment, encodeServiceNameForUrl } from './urlPathSegment';

/**
 * The file a Code-tab URL asks for: `/code/<tenant>/<project>/<service>/<branch>/code/<file>` (the file URL-encoded as
 * one segment, `src%2Fnotes%2FMap.js`), with the line in `#L42` (or `?line=42`). The segment after `/code/` is the
 * route's own trailing `*` (see App.jsx's <Route path="/code/:tenantId?/.../:tab?/*">).
 * Returns `{ path, line }`, nulls when the URL names no file (another tab, or no file yet).
 */
export function codeFileFromRoute(params = {}, location = {}) {
  if (params.tab !== 'code') { return { path: null, line: null }; }
  const raw = params['*'] || '';
  let path = raw;
  // The router already decodes the segment; a second decode only matters for a link encoded twice.
  try { path = decodeURIComponent(raw); } catch { path = raw; }
  path = path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!path || path.split('/').includes('..')) { return { path: null, line: null }; }
  return { path, line: lineFromLocation(location) };
}

/** `#L42` first, then `?line=42`; null without one. */
export function lineFromLocation({ hash = '', search = '' } = {}) {
  const fromHash = /^#L(\d+)/.exec(hash || '');
  const value = fromHash ? Number(fromHash[1]) : Number(new URLSearchParams(search || '').get('line'));
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * The Code-tab URL of a selection: `/code/<tenant>/<project>[/<service>[/<branch>[/<tab>[/<file>]]]]`, the file one
 * encoded segment (only on the `code` tab), the line as `#L42`. `search` is kept as given.
 */
export function codeSelectionUrl({ tenantId, projectId, service, branch, tab, filePath, line, search = '' }) {
  let path = `/code/${tenantId}/${projectId}`;
  if (service) {
    path += `/${encodeServiceNameForUrl(service)}`;
    if (branch) {
      path += `/${encodePathSegment(branch)}`;
      if (tab) {
        path += `/${encodePathSegment(tab)}`;
        if (filePath && tab === 'code') { path += `/${encodeURIComponent(filePath)}`; }
      }
    }
  }
  return `${path}${search || ''}${line ? `#L${line}` : ''}`;
}

/**
 * Where a selection change (Canvas's onSelectionChange) takes the URL, or null when it is there already.
 * The line in the URL (`#L42`) belongs to the URL's file: a sync that names the same file without a line (Canvas only
 * reports service/branch/tab changes, never the line) keeps it. Dropping it there sent a deep link's `#L42` away before
 * the editor had the file open, so the file opened on line 1.
 */
export function selectionSyncTarget({ tenantId, projectId, service, branch, tab, filePath, line, location = {} }) {
  const search = location.search || '';
  const bare = codeSelectionUrl({ tenantId, projectId, service, branch, tab, filePath, search: '' });
  const sameFile = Boolean(filePath) && samePath(bare, location.pathname || '');
  const keepHash = !line && sameFile && /^#L\d+/.test(location.hash || '');
  const hash = line ? `#L${line}` : (keepHash ? location.hash : '');
  return samePath(bare, location.pathname || '') && hash === (location.hash || '') ? null : `${bare}${search}${hash}`;
}

function samePath(a, b) {
  const decode = (value) => { try { return decodeURIComponent(value); } catch { return value; } };
  return decode(a) === decode(b);
}

/**
 * A Files-view link (`?view=files&path=services/app/src/server.js&line=12`, the chat's file names, "Open the code" from
 * Traces and Quality) to a file inside one of the project's services: `{ service, path, line }`, the path relative to
 * that service's folder, the service whose folder is the longest prefix of the path. Null when the path is in no
 * service (the project root, `brain/`): those stay in Project Home's Files view.
 */
export function serviceFileFromFilesLink(search, serviceNames = []) {
  const fullPath = filesPathFromSearch(search);
  if (!fullPath) { return null; }
  let best = null;
  for (const name of serviceNames) {
    if (!name || name === '.' || isBrainPath(name)) { continue; }
    const folder = servicePath(name).replace(/\/+$/, '');
    if (fullPath.startsWith(`${folder}/`) && (!best || folder.length > best.folder.length)) { best = { name, folder }; }
  }
  if (!best) { return null; }
  const path = fullPath.slice(best.folder.length + 1);
  return path ? { service: best.name, path, line: filesLineFromSearch(search) } : null;
}

/** The query string without what the Code file link carried (`tab=code`, the old `view`, `path`, `line`, `preview`), the rest kept. */
export function searchWithoutFilesLink(search) {
  const params = new URLSearchParams(search || '');
  ['view', 'path', 'line', 'preview'].forEach((key) => params.delete(key));
  if (params.get('tab') === 'code') { params.delete('tab'); }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** The name a service goes by in a sentence: `services/web` → `web`, the project root → `the project`. */
export function serviceLabel(serviceName) {
  if (!serviceName || serviceName === '.') { return 'the project'; }
  return String(serviceName).split('/').filter(Boolean).pop() || String(serviceName);
}

function editDistance(a, b) {
  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let diagonal = previous[0];
    previous[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const above = previous[j];
      previous[j] = a[i - 1] === b[j - 1] ? diagonal : 1 + Math.min(diagonal, previous[j], previous[j - 1]);
      diagonal = above;
    }
  }
  return previous[b.length];
}

/**
 * The files closest to a path that is not there (renamed, moved, deleted): the same file name elsewhere first, then
 * the nearest names and paths. At most `limit`.
 */
export function closestPaths(target, paths = [], limit = 3) {
  const wanted = String(target || '').toLowerCase();
  const wantedName = wanted.split('/').pop();
  const wantedStem = wantedName.replace(/\.[^.]+$/, '');
  return paths
    .map((path) => {
      const lower = path.toLowerCase();
      const name = lower.split('/').pop();
      const stem = name.replace(/\.[^.]+$/, '');
      let score = editDistance(wantedName, name) * 3 + editDistance(wanted, lower);
      if (name === wantedName) { score -= 1000; } else if (stem === wantedStem) { score -= 500; } else if (stem.includes(wantedStem) || wantedStem.includes(stem)) { score -= 100; }
      return { path, score };
    })
    .filter(({ path, score }) => score < 0 || editDistance(wantedName, path.toLowerCase().split('/').pop()) <= Math.max(2, Math.floor(wantedName.length / 2)))
    .sort((a, b) => a.score - b.score || a.path.localeCompare(b.path))
    .slice(0, limit)
    .map(({ path }) => path);
}

// Route segments (`/flow/code/<tenant>/<project>/<service>/<branch>/<tab>/...`, `/chat/...`) that once ended up saved
// as "the last opened file" when a URL was parsed wrongly.
const ROUTE_SEGMENTS = new Set(['chat', 'code', 'preview', 'files', 'service', 'agent', 'dashboard', 'logs', 'configuration']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a saved "last opened file" path is really a piece of a URL (a route segment, a conversation id, a
 * `chat/...` path) rather than a file. Files without an extension (Dockerfile, Makefile, LICENSE, .env) are files.
 * @param {string|null|undefined} storedPath
 * @returns {boolean}
 */
export function isCorruptedStoredFilePath(storedPath) {
  if (!storedPath) { return false; }
  if (storedPath.startsWith('chat/')) { return true; }
  if (storedPath.includes('/')) { return false; }
  return ROUTE_SEGMENTS.has(storedPath) || UUID.test(storedPath);
}

/**
 * The same Code URL in the project's own organization: `/code/<from>/<project>…` with `<from>` swapped for `<to>`,
 * search and hash kept. Null when the path is not a `/code/<from>/…` URL or the two are the same (nothing to fix).
 * Used when a link was built with the person's selected organization but the project lives in another one (a
 * workspace can hold projects of several organizations).
 */
export function codeUrlInTenant({ pathname = '', search = '', hash = '' } = {}, fromTenantId, toTenantId) {
  if (!fromTenantId || !toTenantId || fromTenantId === toTenantId) { return null; }
  const prefix = `/code/${encodeURIComponent(fromTenantId)}/`;
  const rawPrefix = `/code/${fromTenantId}/`;
  const start = pathname.startsWith(prefix) ? prefix : (pathname.startsWith(rawPrefix) ? rawPrefix : null);
  if (!start) { return null; }
  return `/code/${encodeURIComponent(toTenantId)}/${pathname.slice(start.length)}${search || ''}${hash || ''}`;
}

/**
 * The address bar's path without the router's basename (`/flow/code/t/p` → `/code/t/p` under `/flow/`), as react-router's
 * `navigate()` wants it: navigate adds the basename again, so `window.location.pathname` as is gives `/flow/flow/…`
 * (no route: a blank page, or Home).
 */
export function routerPathname(pathname = '/', base = import.meta.env?.BASE_URL || '/') {
  const prefix = String(base || '/').replace(/\/+$/, '');
  const path = pathname || '/';
  if (!prefix) { return path; }
  if (path === prefix) { return '/'; }
  return path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path;
}
